import React, { useMemo, useState, useEffect } from 'react';
import IconButton from '../../icons/IconButton';
import IconText from '../../icons/IconText';
import { ICON } from '../../../icons/iconIds';
import SelkirkIcon from '../../icons/SelkirkIcon';
import { useAutoSelectSingleFilterMatch } from '../../../utils/qaqc/useAutoSelectSingleFilterMatch';
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea
} from 'recharts';
import { 
  transformBlanksDriftForChart,
  COLORS
} from '../../../utils/qaqc/chartDataTransform';
import {
  boundsForBlanksLeftAxis,
  boundsForCarryoverPct,
  augmentRowsNumericPlot
} from '../../../utils/qaqc/chartOutliers';
import SurroundingSamplesTable from './SurroundingSamplesTable';

const BlanksContaminationChart = ({ 
  data,
  selectedElement = 'Ag',
  selectedSample = null,
  surroundingSamples = null,
  loadingSurrounding = false,
  onElementChange,
  onPointClick,
  onClearSelection,
  onCopyData,
  externalSampleFilter = null,
  height = 500
}) => {
  const [xAxisType, setXAxisType] = useState('index'); // 'index' or 'date'
  const [showCarryover, setShowCarryover] = useState(false);
  const [sampleFilter, setSampleFilter] = useState('');

  useEffect(() => {
    if (externalSampleFilter == null || externalSampleFilter === '') return;
    setSampleFilter(String(externalSampleFilter));
  }, [externalSampleFilter]);
  
  // Transform data for chart
  const chartData = useMemo(() => {
    return transformBlanksDriftForChart(data, selectedElement);
  }, [data, selectedElement]);
  
  // Filter chart data by sample number
  const filteredChartData = useMemo(() => {
    if (!sampleFilter) return chartData;
    const term = sampleFilter.toLowerCase();
    return chartData.filter(d => 
      d.sampleNumber?.toLowerCase().includes(term) ||
      d.workorderId?.toLowerCase().includes(term)
    );
  }, [chartData, sampleFilter]);

  useAutoSelectSingleFilterMatch(sampleFilter, filteredChartData, onPointClick);
  
  // Calculate statistics (use filtered data)
  const stats = useMemo(() => {
    if (filteredChartData.length === 0) return null;
    
    const values = filteredChartData.map(d => d.value);
    const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
    
    const failures = filteredChartData.filter(d => d.status === 'fail').length;
    const warnings = filteredChartData.filter(d => d.status === 'warning').length;
    const passes = filteredChartData.filter(d => d.status === 'pass').length;
    
    const carryoverGE10 = filteredChartData.filter(d => d.flagCarryoverGE10).length;
    const carryoverGE20 = filteredChartData.filter(d => d.flagCarryoverGE20).length;
    
    // Get control limits from first item (they're all the same)
    const limits = filteredChartData[0];
    
    return {
      count: filteredChartData.length,
      mean: mean.toFixed(5),
      stddev: limits?.stddev.toFixed(5) || 0,
      failures,
      warnings,
      passes,
      passRate: ((passes / chartData.length) * 100).toFixed(1),
      carryoverGE10,
      carryoverGE20,
      upperWarning: limits?.upperWarning,
      lowerWarning: limits?.lowerWarning,
      upperControl: limits?.upperControl,
      lowerControl: limits?.lowerControl
    };
  }, [filteredChartData]);

  const blanksPlotData = useMemo(() => {
    if (filteredChartData.length === 0) return [];
    const s = stats;
    const vals = filteredChartData
      .map((d) => d.value)
      .filter((v) => v != null && !Number.isNaN(v));
    const meanNum =
      s?.mean != null ? parseFloat(s.mean) : null;
    const { lo, hi } = boundsForBlanksLeftAxis(
      vals,
      s?.lowerControl,
      s?.upperControl,
      meanNum
    );
    let rows = augmentRowsNumericPlot(
      filteredChartData,
      'value',
      lo,
      hi,
      (v) => v.toFixed(4)
    );
    if (showCarryover) {
      const cVals = filteredChartData
        .map((d) => d.carryoverPct)
        .filter((v) => v != null && !Number.isNaN(v));
      const cB = boundsForCarryoverPct(cVals);
      rows = augmentRowsNumericPlot(
        rows,
        'carryoverPct',
        cB.lo,
        cB.hi,
        (v) => `${v.toFixed(1)}%`
      );
    }
    return rows;
  }, [filteredChartData, stats, showCarryover]);

  const blanksLeftDomain = useMemo(() => {
    if (blanksPlotData.length === 0) return null;
    const vals = blanksPlotData
      .map((d) => d.plotValue)
      .filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (vals.length === 0) return null;
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = (hi - lo) * 0.03 || 1e-12;
    return [lo - pad, hi + pad];
  }, [blanksPlotData]);

  const carryRightDomain = useMemo(() => {
    if (!showCarryover || blanksPlotData.length === 0) return null;
    const vals = blanksPlotData
      .map((d) => d.plotCarryoverPct)
      .filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (vals.length === 0) return null;
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = (hi - lo) * 0.04 || 0.5;
    return [Math.max(0, lo - pad), hi + pad];
  }, [showCarryover, blanksPlotData]);
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      
      return (
        <div style={{
          background: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '6px',
          padding: '12px',
          color: '#f9fafb',
          fontSize: '0.875rem',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#06b6d4' }}>
            {data.sampleNumber}
          </div>
          <div style={{ fontSize: '0.8125rem', color: '#9ca3af', marginBottom: '8px' }}>
            {data.workorderId} | Seq: {data.sampleSeqNum}
            {data.holeId && ` | ${data.holeId}`}
          </div>
          <div style={{ borderTop: '1px solid #374151', paddingTop: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>{selectedElement} Value:</span>
              <span style={{ fontWeight: 'bold', color: data.status === 'fail' ? COLORS.fail : data.status === 'warning' ? COLORS.warning : COLORS.pass }}>
                {data.value.toFixed(5)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Mean:</span>
              <span style={{ fontWeight: 'bold', color: COLORS.mean }}>{data.mean.toFixed(5)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Z-Score:</span>
              <span style={{ fontWeight: 'bold' }}>{data.zScore.toFixed(2)}</span>
            </div>
            {data.rollingMeanZ20 !== null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
                <span>Rolling Mean (20):</span>
                <span style={{ fontWeight: 'bold', color: COLORS.value }}>{data.rollingMeanZ20.toFixed(3)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Carryover:</span>
              <span style={{ 
                fontWeight: 'bold',
                color: data.carryoverPct > 20 ? COLORS.fail : data.carryoverPct > 10 ? COLORS.warning : COLORS.pass
              }}>
                {data.carryoverPct.toFixed(2)}%
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
              <span>Status:</span>
              <span style={{ 
                fontWeight: 'bold',
                color: data.status === 'fail' ? COLORS.fail : data.status === 'warning' ? COLORS.warning : COLORS.pass
              }}>
                {data.qcStatus}
              </span>
            </div>
            
            {(data.flagCarryoverGE10 || data.flagCarryoverGE20 || data.flagShift8) && (
              <>
                <div style={{ borderTop: '1px solid #374151', marginTop: '8px', paddingTop: '8px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#f59e0b' }}>
                    <IconText icon={ICON.uiWarning} iconSize={14} as="span">Flags:</IconText>
                  </div>
                  {data.flagCarryoverGE20 && (
                    <div style={{ fontSize: '0.75rem', color: '#ef4444', marginBottom: '2px' }}>
                      • Carryover ≥ 20%
                    </div>
                  )}
                  {data.flagCarryoverGE10 && !data.flagCarryoverGE20 && (
                    <div style={{ fontSize: '0.75rem', color: '#fbbf24', marginBottom: '2px' }}>
                      • Carryover ≥ 10%
                    </div>
                  )}
                  {data.flagShift8 && (
                    <div style={{ fontSize: '0.75rem', color: '#fbbf24', marginBottom: '2px' }}>
                      • 8 consecutive same side
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {data.receivedDate && (
            <div style={{ borderTop: '1px solid #374151', marginTop: '8px', paddingTop: '8px', fontSize: '0.75rem', color: '#9ca3af' }}>
              Date: {new Date(data.receivedDate).toLocaleDateString()}
            </div>
          )}
        </div>
      );
    }
    return null;
  };
  
  // Custom dot component for scatter points
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    
    if (!payload || cx === undefined || cy === undefined) return null;
    
    const getColor = () => {
      if (payload.status === 'fail') return COLORS.fail;
      if (payload.status === 'warning') return COLORS.warning;
      return COLORS.pass;
    };
    
    const handleClick = () => {
      if (onPointClick) {
        onPointClick(payload.raw);
      }
    };
    
    const hasCarryoverFlag = payload.flagCarryoverGE10 || payload.flagCarryoverGE20;
    const r = hasCarryoverFlag ? 7 : 5;
    const clip = payload.valueAxisLabel;
    const clipHigh =
      clip && payload.value > (payload.plotValue ?? payload.value);
    let flagY = cy - 12;
    if (clip) {
      flagY = clipHigh ? cy - r - 18 : cy - 12;
    }
    
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={hasCarryoverFlag ? 7 : 5}
          fill={getColor()}
          stroke={payload.flagCarryoverGE20 ? '#ef4444' : payload.flagCarryoverGE10 ? '#fbbf24' : 'none'}
          strokeWidth={hasCarryoverFlag ? 2 : 0}
          style={{ cursor: onPointClick ? 'pointer' : 'default' }}
          onClick={handleClick}
          opacity={0.8}
        />
        {hasCarryoverFlag && (
          <text
            x={cx}
            y={flagY}
            fill={payload.flagCarryoverGE20 ? '#ef4444' : '#fbbf24'}
            fontSize="12"
            fontWeight="bold"
            textAnchor="middle"
          >
            <SelkirkIcon name={ICON.uiWarning} size={16} decorative />
          </text>
        )}
        {clip && (
          <text
            x={cx}
            y={clipHigh ? cy + 14 : cy - 22 - (hasCarryoverFlag ? 6 : 0)}
            fill="#e5e7eb"
            fontSize={9}
            textAnchor="middle"
          >
            {clip}
          </text>
        )}
      </g>
    );
  };
  
  return (
    <div className="blanks-contamination-chart">
      {/* Filter Controls */}
      <div style={{ 
        padding: '1rem',
        background: '#111827',
        border: '1px solid #374151',
        borderRadius: '8px',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Element Filter */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Element:</label>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {['Ag', 'Au', 'Cu'].map(element => (
                <button
                  key={element}
                  onClick={() => onElementChange && onElementChange(element)}
                  style={{
                    padding: '0.25rem 0.75rem',
                    background: selectedElement === element ? '#06b6d4' : '#374151',
                    color: selectedElement === element ? '#0c4a6e' : '#f9fafb',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: selectedElement === element ? '600' : '400',
                    cursor: 'pointer'
                  }}
                >
                  {element}
                </button>
              ))}
            </div>
          </div>
          
          {/* X-Axis Toggle */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>X-Axis:</label>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button
                onClick={() => setXAxisType('index')}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: xAxisType === 'index' ? '#06b6d4' : '#374151',
                  color: xAxisType === 'index' ? '#0c4a6e' : '#f9fafb',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: xAxisType === 'index' ? '600' : '400',
                  cursor: 'pointer'
                }}
              >
                Sequence
              </button>
              <button
                onClick={() => setXAxisType('date')}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: xAxisType === 'date' ? '#06b6d4' : '#374151',
                  color: xAxisType === 'date' ? '#0c4a6e' : '#f9fafb',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: xAxisType === 'date' ? '600' : '400',
                  cursor: 'pointer'
                }}
              >
                Seq Num
              </button>
            </div>
          </div>
          
          {/* Show Carryover Toggle */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              color: '#9ca3af',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={showCarryover}
                onChange={(e) => setShowCarryover(e.target.checked)}
              />
              Show Carryover %
            </label>
          </div>
          
          {/* Sample Filter */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flex: 1 }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Sample Filter:</label>
            <input
              type="text"
              placeholder="Search by sample number..."
              value={sampleFilter}
              onChange={(e) => setSampleFilter(e.target.value)}
              style={{
                padding: '0.25rem 0.75rem',
                background: '#374151',
                color: '#f9fafb',
                border: '1px solid #4b5563',
                borderRadius: '4px',
                fontSize: '0.875rem',
                flex: 1,
                minWidth: '200px'
              }}
            />
            {sampleFilter && (
              <IconButton
                icon={ICON.ddClose}
                onClick={() => setSampleFilter('')}
                ariaLabel="Clear sample filter"
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Statistics Panel */}
      {stats && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '0.75rem',
          padding: '1rem',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '8px',
          marginBottom: '1rem',
          fontSize: '0.875rem'
        }}>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Total Blanks</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#f9fafb' }}>{stats.count}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Mean Value</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#06b6d4' }}>{stats.mean}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Pass</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.pass }}>{stats.passes}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Warning</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.warning }}>{stats.warnings}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Fail</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.fail }}>{stats.failures}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Pass Rate</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#f9fafb' }}>{stats.passRate}%</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Carryover ≥10%</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#fbbf24' }}>{stats.carryoverGE10}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Carryover ≥20%</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#ef4444' }}>{stats.carryoverGE20}</div>
          </div>
        </div>
      )}
      
      {/* Chart */}
      {filteredChartData.length === 0 ? (
        <div style={{ 
          padding: '3rem',
          textAlign: 'center',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '8px',
          color: '#9ca3af'
        }}>
          <p>No data available for {selectedElement}.</p>
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Try selecting a different element{sampleFilter ? ' or adjust your filter' : ''}.
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart 
            data={blanksPlotData} 
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
            
            <XAxis 
              dataKey={xAxisType === 'index' ? 'index' : 'sampleSeqNum'}
              stroke={COLORS.text}
              tick={{ fill: COLORS.text, fontSize: 11 }}
              label={{
                value: xAxisType === 'index' ? 'Sample Order (by Date)' : 'Sample Seq Num',
                position: 'insideBottom',
                offset: -10,
                style: { fill: COLORS.text, fontSize: 12 }
              }}
            />
            
            <YAxis 
              yAxisId="left"
              stroke={COLORS.text}
              tick={{ fill: COLORS.text, fontSize: 11 }}
              label={{ 
                value: `${selectedElement} Value`, 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: COLORS.text, fontSize: 12 }
              }}
              domain={blanksLeftDomain || ['auto', 'auto']}
              allowDataOverflow
            />
            
            {showCarryover && (
              <YAxis 
                yAxisId="right"
                orientation="right"
                stroke={COLORS.warning}
                tick={{ fill: COLORS.warning, fontSize: 11 }}
                label={{ 
                  value: 'Carryover %', 
                  angle: 90, 
                  position: 'insideRight',
                  style: { fill: COLORS.warning, fontSize: 12 }
                }}
                domain={carryRightDomain || ['auto', 'auto']}
                allowDataOverflow
              />
            )}
            
            {/* Reference line for mean */}
            <ReferenceLine yAxisId="left" y={stats?.mean} stroke={COLORS.mean} strokeDasharray="5 5" strokeWidth={1} label={{ value: 'Mean', fill: COLORS.mean, fontSize: 10 }} />
            
            {/* Warning zone */}
            {stats && (
              <>
                <ReferenceArea yAxisId="left" y1={stats.lowerWarning} y2={stats.upperWarning} fill="#f59e0b" fillOpacity={0.1} />
                <ReferenceLine yAxisId="left" y={stats.upperWarning} stroke={COLORS.warning} strokeDasharray="3 3" strokeWidth={1} label={{ value: '+2SD', fill: COLORS.warning, fontSize: 10 }} />
                <ReferenceLine yAxisId="left" y={stats.lowerWarning} stroke={COLORS.warning} strokeDasharray="3 3" strokeWidth={1} label={{ value: '-2SD', fill: COLORS.warning, fontSize: 10 }} />
                <ReferenceLine yAxisId="left" y={stats.upperControl} stroke={COLORS.fail} strokeDasharray="3 3" strokeWidth={1} label={{ value: '+3SD', fill: COLORS.fail, fontSize: 10 }} />
                <ReferenceLine yAxisId="left" y={stats.lowerControl} stroke={COLORS.fail} strokeDasharray="3 3" strokeWidth={1} label={{ value: '-3SD', fill: COLORS.fail, fontSize: 10 }} />
              </>
            )}
            
            <Tooltip content={<CustomTooltip />} />
            
            {/* Mean line */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="mean"
              stroke={COLORS.mean}
              strokeWidth={2}
              dot={false}
              name="Mean"
            />
            
            {/* Blank value points */}
            <Scatter
              yAxisId="left"
              dataKey="plotValue"
              shape={<CustomDot />}
              name="Blank Value"
            />
            
            {/* Carryover line (if enabled) */}
            {showCarryover && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="plotCarryoverPct"
                stroke={COLORS.warning}
                strokeWidth={1}
                strokeDasharray="5 5"
                dot={false}
                name="Carryover %"
              />
            )}
            
            <Legend 
              wrapperStyle={{ paddingTop: '10px' }}
              content={() => (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: '1.5rem',
                  fontSize: '0.875rem',
                  color: '#9ca3af',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS.pass }} />
                    Pass
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS.warning }} />
                    Warning
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS.fail }} />
                    Fail
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <SelkirkIcon name={ICON.uiWarning} size={16} decorative />
                    Carryover ≥10%
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <SelkirkIcon name={ICON.uiError} size={16} decorative />
                    Carryover ≥20%
                  </span>
                </div>
              )}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      
      {/* Helper text */}
      {onPointClick && filteredChartData.length > 0 && !selectedSample && (
        <div style={{ 
          marginTop: '0.75rem',
          padding: '0.75rem',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '6px',
          fontSize: '0.875rem', 
          color: '#9ca3af', 
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem'
        }}>
          <SelkirkIcon name={ICON.uiPointerHint} size={20} decorative />
          <span>
            <strong style={{ color: '#06b6d4' }}>Click any data point</strong> to investigate contamination events
          </span>
        </div>
      )}
      
      {/* Selected Sample Details Panel */}
      {selectedSample && (
        <div style={{ 
          marginTop: '1rem',
          background: '#111827',
          border: '2px solid #06b6d4',
          borderRadius: '8px',
          padding: '1rem'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h4 style={{ margin: 0, color: '#06b6d4' }}>
              Selected Sample Details: {selectedSample.sample_number}
            </h4>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                onClick={onCopyData}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#06b6d4',
                  color: '#f9fafb',
                  border: '1px solid #0891b2',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                <IconText icon={ICON.uiClipboardList} iconSize={16}>Copy Data</IconText>
              </button>
              <button 
                onClick={onClearSelection}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#374151',
                  color: '#f9fafb',
                  border: '1px solid #4b5563',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                <IconText icon={ICON.uiClose} iconSize={16}>Clear Selection</IconText>
              </button>
            </div>
          </div>
          
          <table className="sample-details-table">
            <tbody>
              <tr>
                <th>Workorder ID</th>
                <td>{selectedSample.workorder_id}</td>
                <th>Sample Sequence</th>
                <td>{selectedSample.sample_sequence}</td>
              </tr>
              <tr>
                <th>Element</th>
                <td>{selectedSample.element}</td>
                <th>Hole ID</th>
                <td>{selectedSample.hole_id || 'N/A'}</td>
              </tr>
              <tr>
                <th>Value</th>
                <td>{parseFloat(selectedSample.value).toFixed(5)}</td>
                <th>Mean</th>
                <td>{parseFloat(selectedSample.mean_value).toFixed(5)}</td>
              </tr>
              <tr>
                <th>Z-Score</th>
                <td style={{ 
                  color: Math.abs(parseFloat(selectedSample.z_score)) > 3 ? '#ef4444' : '#f9fafb'
                }}>
                  {parseFloat(selectedSample.z_score).toFixed(3)}
                </td>
                <th>QC Status</th>
                <td style={{ 
                  color: selectedSample.qc_status?.includes('FAIL') ? '#ef4444' : '#10b981'
                }}>
                  {selectedSample.qc_status}
                </td>
              </tr>
              <tr>
                <th>Carryover %</th>
                <td style={{ 
                  color: parseFloat(selectedSample.crushing_carryover_pct) > 20 ? '#ef4444' : 
                         parseFloat(selectedSample.crushing_carryover_pct) > 10 ? '#f59e0b' : '#10b981'
                }}>
                  {parseFloat(selectedSample.crushing_carryover_pct).toFixed(2)}%
                </td>
                <th>Parent Sample</th>
                <td>{selectedSample.parent_sample_number || 'N/A'}</td>
              </tr>
              <tr>
                <th>Received Date</th>
                <td>{selectedSample.received_date ? new Date(selectedSample.received_date).toLocaleString() : 'N/A'}</td>
                <th>Lab Code</th>
                <td>{selectedSample.lab_code || 'N/A'}</td>
              </tr>
              <tr>
                <th>Warning Limits</th>
                <td>{parseFloat(selectedSample.lower_warning_2sd).toFixed(5)} to {parseFloat(selectedSample.upper_warning_2sd).toFixed(5)}</td>
                <th>Control Limits</th>
                <td>{parseFloat(selectedSample.lower_control_3sd).toFixed(5)} to {parseFloat(selectedSample.upper_control_3sd).toFixed(5)}</td>
              </tr>
              <tr>
                <th>Exceeds Warning</th>
                <td style={{ color: selectedSample.exceeds_warning_limit ? '#f59e0b' : '#10b981' }}>
                  {selectedSample.exceeds_warning_limit ? 'YES' : 'NO'}
                </td>
                <th>Exceeds Control</th>
                <td style={{ color: selectedSample.exceeds_control_limit ? '#ef4444' : '#10b981' }}>
                  {selectedSample.exceeds_control_limit ? 'YES' : 'NO'}
                </td>
              </tr>
              {(selectedSample.flag_carryover_ge_10pct || selectedSample.flag_carryover_ge_20pct) && (
                <tr>
                  <th>Carryover Flags</th>
                  <td colSpan="3" style={{ color: '#f59e0b' }}>
                    {selectedSample.flag_carryover_ge_20pct && (
                      <IconText icon={ICON.uiWarning} iconSize={12} as="span">
                        Carryover ≥ 20%
                      </IconText>
                    )}
                    {selectedSample.flag_carryover_ge_10pct && !selectedSample.flag_carryover_ge_20pct && (
                      <IconText icon={ICON.uiWarning} iconSize={12} as="span">
                        Carryover ≥ 10%
                      </IconText>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          
          <SurroundingSamplesTable 
            data={surroundingSamples}
            loading={loadingSurrounding}
            selectedSample={selectedSample}
          />
        </div>
      )}
    </div>
  );
};

export default BlanksContaminationChart;
